import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IWhatsAppMessage,
  IWhatsAppSettings,
  WhatsAppMediaUrl,
} from 'services/whatsAppMessaging/types';
import { RootState } from '../index';
import {
  ThunkGetWhatsAppChatMessages,
  ThunkGetWhatsAppSettings,
} from 'store/whatsAppMessaging/thunks';

interface IInitialState {
  history: IWhatsAppMessage[];
  settings: IWhatsAppSettings | null;
}

const initialState: IInitialState = {
  history: [],
  settings: null,
};

const whatsAppMessaging = createSlice({
  name: 'whatsAppMessaging',
  reducers: {
    addWhatsAppHistory: (state, action: PayloadAction<IWhatsAppMessage>) => {
      state.history.push(action.payload);
    },
    changeWhatsAppHistoryItemState: (
      state,
      action: PayloadAction<{
        id: string;
        loading: boolean;
        message?: string;
        mediaUrls?: WhatsAppMediaUrl[];
      }>,
    ) => {
      state.history = state.history.map(i =>
        i.id === action.payload.id
          ? {
              ...i,
              loading: action.payload.loading,
              message: action.payload?.message ?? i.message,
              mediaUrls: action.payload?.mediaUrls ?? i.mediaUrls,
            }
          : i,
      );
    },
  },
  initialState,
  extraReducers: builder => {
    builder.addCase(ThunkGetWhatsAppChatMessages.fulfilled, (state, action) => {
      state.history = action.payload;
    });
    builder.addCase(ThunkGetWhatsAppSettings.fulfilled, (state, action) => {
      state.settings = action.payload;
    });
  },
});

export default whatsAppMessaging.reducer;
export const { addWhatsAppHistory, changeWhatsAppHistoryItemState } = whatsAppMessaging.actions;
export const WhatsAppHistorySelector = (state: RootState): IWhatsAppMessage[] =>
  state.whatsAppMessaging.history;

export const hasWhatsAppSettingsSelector = (state: RootState): boolean => {
  const s = state.whatsAppMessaging.settings;
  return s != null && (Array.isArray(s) ? s.length > 0 : !!s?.businessAccountId);
};

export const hasWhatsAppSettingsForEuvSelector = (state: RootState): boolean => {
  const settings = state.whatsAppMessaging.settings;
  return (
    settings != null &&
    settings.defaultEuvTemplateName != null &&
    settings.defaultEuvTemplateName !== ''
  );
};

/**
 * Stricter than {@link hasWhatsAppSettingsSelector}: true only when WhatsApp is configured well
 * enough to actually SEND a template message — Business Account id (template lookup), Phone Number id
 * (the send endpoint), an auth token, and a template name. Used to gate the "send a link" WhatsApp
 * channel so it isn't offered when a partial config would fail at send time.
 */
export const isWhatsAppSendReadySelector = (state: RootState): boolean => {
  const raw = state.whatsAppMessaging.settings;
  const s = Array.isArray(raw) ? raw[0] : raw;
  return (
    s != null &&
    !!s.businessAccountId &&
    !!s.phoneNumberId &&
    !!s.authToken &&
    !!(s.defaultEuvTemplateName || s.defaultTemplateName)
  );
};
